/**
 * Bridge from purchase wizard answers to the authoritative referral form state
 * (WI-TV-W6-003).
 *
 * Like the refinance projection this is a direct copy keyed by the
 * `ReferralFormState` field names: `buildPayload` and the LMD mapper keep owning
 * shaping and mapping, so purchase answers reach the wire through the shared
 * boundary.
 *
 * The two places it is more than a copy are both existing purchase contracts:
 * the home-search answer becomes `propertyStillLooking`, which is what makes the
 * payload omit a manual address the borrower can no longer see, and the
 * application-level military answer is tri-state on the wire (asserted true,
 * asserted false, or absent) while form state models the assertion and its value
 * separately. An unanswered or "Not sure" application asserts neither, and a
 * component is only carried for an affirmative answer, so no stale, coalesced,
 * null, or guessed value can be emitted.
 *
 * The optional co-borrower tail (WI-TV-W7-002) projects through the same shared
 * helper the refinance journey uses, so the two journeys emit the same optional
 * `coBorrower` for the same answers. There is deliberately no co-borrower
 * military status, employment, or income projection: none of those answers
 * exists, and the military question stays application-level.
 */
import { emptyFormState, type ReferralFormState } from "../models/referral";
import {
  CREDIT_SCORE_RANGE_KEY,
  EMAIL_KEY,
  FIRST_NAME_KEY,
  LAST_NAME_KEY,
  MILITARY_ANSWER_KEY,
  MILITARY_COMPONENT_KEY,
  PHONE_KEY,
  phoneDigits,
} from "./borrower-steps";
import {
  ESTIMATED_VALUE_KEY,
  LOAN_AMOUNT_KEY,
  PROPERTY_CITY_KEY,
  PROPERTY_STATE_KEY,
  PROPERTY_STREET_KEY,
  PROPERTY_TYPE_KEY,
  PROPERTY_ZIP_KEY,
} from "./refinance-steps";
import { DOWN_PAYMENT_KEY, HOME_SEARCH_FOUND, HOME_SEARCH_STATUS_KEY } from "./purchase-steps";
import { applyCoBorrowerFormState } from "./coborrower-steps";

/** Projects a purchase wizard value snapshot onto a {@link ReferralFormState}. */
export function toPurchaseFormState(values: Readonly<Record<string, string>>): ReferralFormState {
  const state = emptyFormState();
  const read = (key: string): string => values[key] ?? "";

  state.loanPurpose = "purchase";
  state.propertyState = read(PROPERTY_STATE_KEY);
  state.propertyType = read(PROPERTY_TYPE_KEY);
  state.estimatedValue = read(ESTIMATED_VALUE_KEY);
  state.loanAmount = read(LOAN_AMOUNT_KEY);
  state.downPayment = read(DOWN_PAYMENT_KEY);

  // A borrower who is still looking has no address on the application, so the
  // payload omits street/city/ZIP entirely rather than carrying hidden text.
  state.propertyStillLooking = read(HOME_SEARCH_STATUS_KEY) !== HOME_SEARCH_FOUND;
  state.propertyStreetAddress = read(PROPERTY_STREET_KEY);
  state.propertyCity = read(PROPERTY_CITY_KEY);
  state.propertyZipCode = read(PROPERTY_ZIP_KEY);

  state.borrower.firstName = read(FIRST_NAME_KEY).trim();
  state.borrower.lastName = read(LAST_NAME_KEY).trim();
  state.borrower.email = read(EMAIL_KEY).trim();
  // The payload always carries the ten digits the borrower intended, whatever
  // formatting characters they typed around them.
  state.borrower.phone = phoneDigits(read(PHONE_KEY));
  state.borrower.creditScoreRange = read(CREDIT_SCORE_RANGE_KEY);

  const answer = read(MILITARY_ANSWER_KEY);
  state.militaryAnswered = answer === "yes" || answer === "no";
  state.isMilitary = answer === "yes";
  state.militaryBranch = answer === "yes" ? read(MILITARY_COMPONENT_KEY) : "";

  applyCoBorrowerFormState(state, values);
  return state;
}
